import { DialogContent, DialogTitle, Typography } from "@mui/material";
import { Dispatch, SetStateAction, useEffect, useRef, useState } from "react";
import { useForm, useWatch } from "react-hook-form";
import { CommonDialog } from "../../../../components/common/CommonDialog";
import { IAppointmentData } from "../../../../utils/interfaces";
import { Steps } from "../../types";
import { ConfirmAppointment } from "./ConfirmAppointment";
import { ConfirmRelationship } from "./ConfirmRelationship";
import { CreatePatient } from "./CreatePatient";
import { SelectAppointmentActors } from "./SelectAppointmentActors";
import { SelectAppointmentPatient } from "./SelectAppointmentPatient";
import { PaymentMethod } from "./PaymentMethod";
import { DomicilioAddressStep } from "./DomicilioAddressStep";
import { currentFeatures } from "@/configs/Settings";
import { useLocationContext } from "@/contexts/LocationContext";
import { useFhirData } from "@/utils/useFhirData";
import { EVOLUTION_TYPE_SERVICE } from "@/components/schedule/evolution/constants";

interface IProps {
  setModalOpen: Function;
  isOpen: boolean;
  row?: any;
  walkin?: boolean;
  isShortCutAppointment?: boolean;
  setRefetchDashboard?: Dispatch<SetStateAction<boolean>>;
  cpfT: any;
  /** Pré-seleciona e trava o tipo (ex.: EVOLUTION no CTA da agenda clínica). */
  initialTypeService?: string;
}

// Títulos explícitos no fluxo humano evitam confusão paciente/dependente vs responsável.
export const stepTitles: { [key in Steps]: string } = {
  [Steps.S1SelectPatient]: "Selecionar Paciente",
  [Steps.S2CreateResponsible]:"Cadastrar responsável legal",
  [Steps.S3CreatePatient]:"Cadastrar paciente",
  [Steps.S4ConfirmRelationship]: "Confirmação do responsável legal",
  [Steps.S5SelectAppointmentActors]: "Selecione o profissional",
  [Steps.S5bDomicilioAddress]: "Endereço do atendimento",
  [Steps.S6ConfirmAppointment]: "Confirmar agendamento",
  [Steps.S7PaymentMethod]: "Forma de Pagamento",
};

export function CreateAppointmentDialog({
  setModalOpen,
  isOpen,
  row,
  walkin: initialWalkin,
  setRefetchDashboard,
  cpfT,
  isShortCutAppointment: initialIsShortCutAppointment,
  initialTypeService,
}: IProps) {
  const defaultValues = {
    cpf: cpfT,
    typeService: initialTypeService,
    professionalOptions: undefined,
    cpfResponsible: undefined,
    gender: undefined,
    schedule: undefined,
    relatedName: undefined,
  };

  const [page, setPage] = useState(Steps.S1SelectPatient);
  const [dataResponsible, setDataResponsible] = useState<fhir5.Patient>();
  const [data, setData] = useState<any>();
  const [walkin, setWalkin] = useState<boolean | undefined>(initialWalkin);
  const [isShortCutAppointment, setIsShortCutAppointment] = useState<
    boolean | undefined
  >(initialIsShortCutAppointment);
  // Contexto de locations para detectar o canal de atendimento (domicílio, etc.)
  const { location: locationList } = useLocationContext();

  // Indica se a agenda selecionada é do tipo domicílio.
  // Quando true, exibe a etapa de endereço (S5bDomicilioAddress) antes da confirmação.
  const [isDomicilioSchedule, setIsDomicilioSchedule] = useState(false);

  // Flag que indica que o usuário já preencheu o endereço.
  // Impede que o useEffect redirecione para S5bDomicilioAddress em loop
  // após o usuário clicar em "Próximo" na etapa de endereço.
  const domicilioAddressCollected = useRef(false);

  const [scheduleActors, setScheduleActors] = useState<any>(
    row?.scheduleActors
  );

  const methods = useForm<IAppointmentData>({ defaultValues });
  const { handleSubmit, control, setValue, reset, watch, trigger } = methods;

  let cpfValue = useWatch({ name: "cpf", control });
  let cpfResponsible = useWatch({ name: "cpfResponsible", control });
  let typeService = useWatch({ name: "typeService", control });

  // INVERSÃO SEMÂNTICA APENAS para defaultApp
  // Quando é defaultApp=TRUE (app de saúde humana):
  //   - campo "cpf" → representa PACIENTE → passa para CreatePatient type="patient"
  //   - campo "cpfResponsible" → representa RESPONSÁVEL → passa para CreatePatient type="responsible"
  //
  // Quando é defaultApp=FALSE (app veterinário):
  //   - campo "cpf" → CPF do tutor → passa para CreatePatient type="patient"
  //   - campo "cpfResponsible" → ID do pet → passa para CreatePatient type="responsible"
  //   → NÃO invertemos, já vem na ordem correta
  const actualPatientCpf = currentFeatures?.defaultApp
    ? cpfValue
    : cpfResponsible;
  const actualResponsibleCpf = currentFeatures?.defaultApp
    ? cpfResponsible
    : cpfValue;

  const watchAllFields = watch();

  useEffect(() => {
    if (initialTypeService) {
      setValue("typeService", initialTypeService);
    }
  }, [initialTypeService, setValue]);

  useEffect(() => {
    if (typeService === "WALKIN") {
      setWalkin(true);
      setIsShortCutAppointment(true);
    } else if (typeService === EVOLUTION_TYPE_SERVICE && !row) {
      // Evolução sem vaga pré-selecionada: precisa escolher profissional, agenda e horário.
      setWalkin(false);
      setIsShortCutAppointment(true);
    } else if (typeService !== "WALKIN" && isShortCutAppointment) {
      setWalkin(false);
      setIsShortCutAppointment(true);
    } else {
      setWalkin(false);
      setIsShortCutAppointment(false);
    }
  }, [typeService]);

  // Intercepta toda navegação para S6ConfirmAppointment.
  // Se o endereço ainda não foi coletado (addressCollected = false),
  // verifica a Location dos scheduleActors para saber se é domicílio.
  // Caso seja, redireciona para S5bDomicilioAddress antes de confirmar.
  useEffect(() => {
    if (page !== Steps.S6ConfirmAppointment) return;
    if (domicilioAddressCollected.current) return; // já coletou endereço, pode seguir

    // Usa os actors da agenda — podem vir do row (AvailableSlots) ou do estado setScheduleActors
    const actors = scheduleActors || row?.scheduleActors;
    if (!actors) return;

    // Encontra o ator Location dentro dos actors da agenda
    const locationActor = useFhirData.findActor(actors, "Location");
    const locationId = locationActor?.reference?.split("/")?.[1];
    if (!locationId) return;

    // Busca o recurso Location completo no contexto para ler as extensions
    const locationResource = locationList?.find(
      (loc: any) => loc?.resource?.id === locationId
    )?.resource;

    // Lê a extension attendance-channel para verificar o canal de atendimento
    const channelExt = locationResource?.extension?.find((ext: any) =>
      ext.url.includes("attendance-channel")
    );

    if (channelExt?.valueCode === "domicilio") {
      // É domicílio: redireciona para coletar o endereço antes de confirmar
      setIsDomicilioSchedule(true);
      setPage(Steps.S5bDomicilioAddress);
    }
  }, [page, scheduleActors, locationList]);

  return (
    <CommonDialog handleClose={() => setModalOpen(false)} isOpen={isOpen}>
      <DialogTitle textAlign="center">
        <Typography variant="h5" fontWeight={600}>
          {page === Steps.S1SelectPatient
            ? walkin
              ? "Encaixe"
              : typeService === EVOLUTION_TYPE_SERVICE
                ? "Evolução"
                : "Realizar atendimento"
            : stepTitles[page]}
        </Typography>
      </DialogTitle>
      <DialogContent>
        {page === Steps.S1SelectPatient && (
          <SelectAppointmentPatient
            control={control}
            walkin={walkin}
            cpfT={cpfT}
            setModalOpen={setModalOpen}
            setPage={setPage}
            handleSubmit={handleSubmit}
            setValue={setValue}
            lockTypeService={initialTypeService}
          />
        )}

        {page === Steps.S2CreateResponsible && (
          <CreatePatient
            type="responsible"
            cpf={actualResponsibleCpf}
            control={control}
            handleSubmit={handleSubmit}
            navigateToNextPage={() => setPage(Steps.S3CreatePatient)}
            setValue={setValue}
            setPage={setPage}
            row={row}
            walkin={walkin}
            reset={reset}
            setDataResponsible={setDataResponsible}
            isShortCutAppointment={isShortCutAppointment}
          />
        )}

        {page === Steps.S3CreatePatient && (
          <CreatePatient
            type="patient"
            cpf={actualPatientCpf}
            control={control}
            handleSubmit={handleSubmit}
            navigateToNextPage={() => setPage(Steps.S5SelectAppointmentActors)}
            setValue={setValue}
            setPage={setPage}
            row={row}
            walkin={walkin}
            reset={reset}
            dataResponsible={dataResponsible}
            watchAllFields={watchAllFields}
            isShortCutAppointment={isShortCutAppointment}
          />
        )}

        {page === Steps.S4ConfirmRelationship && (
          <ConfirmRelationship
            control={control}
            handleSubmit={handleSubmit}
            navigateToNextPage={() =>
              setPage(
                isShortCutAppointment || walkin
                  ? Steps.S5SelectAppointmentActors
                  : Steps.S6ConfirmAppointment
              )
            }
            onBackButtonCLick={() => {
              reset();
              setPage(Steps.S1SelectPatient);
            }}
            dataResponsible={dataResponsible}
          />
        )}

        {page === Steps.S5SelectAppointmentActors && (
          <SelectAppointmentActors
            setScheduleActors={setScheduleActors}
            control={control}
            handleSubmit={handleSubmit}
            setModalOpen={setModalOpen}
            setValue={setValue}
            setData={setData}
            navigateToNextPage={(isDomicilio: boolean) => {
              // Salva a flag para usar na navegação de volta do step de confirmação
              setIsDomicilioSchedule(isDomicilio);
              // Domicílio exige etapa de endereço antes de confirmar
              setPage(isDomicilio ? Steps.S5bDomicilioAddress : Steps.S6ConfirmAppointment);
            }}
            appointmentType={watchAllFields.typeService}
          />
        )}

        {/* Etapa de endereço: exibida apenas para agendas de domicílio */}
        {page === Steps.S5bDomicilioAddress && (
          <DomicilioAddressStep
            control={control}
            setValue={setValue}
            trigger={trigger}
            onNext={() => {
              // Marca que o endereço foi coletado para evitar loop no useEffect
              domicilioAddressCollected.current = true;
              setPage(Steps.S6ConfirmAppointment);
            }}
            onBack={() => setPage(Steps.S5SelectAppointmentActors)}
          />
        )}

        {page === Steps.S6ConfirmAppointment && (
          <ConfirmAppointment
            row={row || data}
            setValue={setValue}
            scheduleActors={scheduleActors}
            control={control}
            walkin={walkin}
            setModalOpen={setModalOpen}
            setTabRefetch={row?.setTabRefetch || setRefetchDashboard}
            handleSubmit={handleSubmit}
            handleNext={() => setPage(Steps.S7PaymentMethod)}
            onBackButtonClick={() => {
              // Se é domicílio, volta para a etapa de endereço
              if (isDomicilioSchedule) {
                setPage(Steps.S5bDomicilioAddress);
              } else {
                setPage(
                  isShortCutAppointment || walkin
                    ? Steps.S5SelectAppointmentActors
                    : Steps.S1SelectPatient
                );
              }
            }}
          />
        )}

        {page === Steps.S7PaymentMethod && (
          <PaymentMethod
            control={control}
            onBackButtonClick={() => setPage(Steps.S6ConfirmAppointment)}
            handleSubmit={handleSubmit}
            setValue={setValue}
            setModalOpen={setModalOpen}
            setTabRefetch={row?.setTabRefetch || setRefetchDashboard}
            walkin={walkin ?? false}
          />
        )}
      </DialogContent>
    </CommonDialog>
  );
}
